import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Badge, 
  Spinner,
  InputGroup,
  Form,
  Row,
  Col
} from 'react-bootstrap';
import { 
  FaVideo, 
  FaFileAlt, 
  FaLink,
  FaSearch
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';
import { useAuth } from '../../../contexts/AuthContext';
import VltDocumentViewer from '../../../common/components/vlt/VltDocumentViewer';
import { isHttpUrl, getMeetingDateStatus, formatMeetingDatetime, getInstructorDisplayName } from '../../../common/utils/vltForm';

const UserVltList = () => {
  const { user } = useAuth();
  const [vlts, setVlts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [viewerVlt, setViewerVlt] = useState(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);

  useEffect(() => {
    fetchVlts();
  }, [filterType]);

  const fetchVlts = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm,
        type: filterType !== 'all' ? filterType : '',
        limit: 100
      };
      
      // Use getUserVlts to get VLTs with progress tracking
      const response = await api.vlt.getUserVlts(params);
      if (response.data.success) {
        setVlts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching VLTs:', error);
      toast.error('Failed to fetch VLTs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredVlts = vlts.filter(vlt => {
    const matchesSearch = vlt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vlt.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return <FaVideo className="text-primary" />;
      case 'document':
        return <FaFileAlt className="text-success" />;
      case 'link':
        return <FaLink className="text-info" />;
      default:
        return null;
    }
  };

  const handleAccessVlt = async (vlt) => {
    if (vlt.type === 'link' && getMeetingDateStatus(vlt.meeting_datetime) !== 'today') {
      return;
    }

    try {
      await api.vlt.trackAccess(vlt.id, 'view', {
        timestamp: new Date().toISOString(),
        type: vlt.type
      });
      fetchVlts();
    } catch (error) {
      console.error('Error tracking VLT access:', error);
    }

    if (vlt.type === 'document') {
      const docUrl = vlt.url_or_path?.trim();
      if (isHttpUrl(docUrl)) {
        window.open(docUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      setViewerVlt(vlt);
      setShowDocumentViewer(true);
      return;
    }

    const resourceUrl = vlt.url_or_path?.startsWith('http')
      ? vlt.url_or_path
      : getAssetUrl(vlt.url_or_path);

    window.open(resourceUrl, '_blank', 'noopener,noreferrer');
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'video':
        return 'primary';
      case 'document':
        return 'success';
      case 'link':
        return 'info';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading VLTs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-5 py-4 sm:p-6 border border-purple-100 shadow-xl">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl shadow-md">
              <i className="fas fa-laptop"></i>
            </div>
            <div>
              <h2 className="text-left text-2xl sm:text-2xl font-bold text-gray-900 mb-1">Virtual-Led Training</h2>
              <p className="text-gray-600 text-sm sm:text-base m-0">
                Access additional learning resources and materials
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6 mb-6">
        <Row className="g-3">
          <Col md={6} sm={12}>
            <InputGroup>
              <InputGroup.Text className="bg-white border-2 border-gray-200">
                <FaSearch className="text-purple-600" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search VLTs..."
                value={searchTerm}
                onChange={handleSearch}
                className="border-2 border-gray-200 focus:border-purple-500"
              />
            </InputGroup>
          </Col>
          <Col md={4} sm={12}>
            <Form.Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border-2 border-gray-200 focus:border-purple-500"
            >
              <option value="all">All Types</option>
              <option value="video">Video</option>
              <option value="document">Document</option>
              <option value="link">Meeting Link</option>
            </Form.Select>
          </Col>
        </Row>
      </div>

      {/* VLTs Grid */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6">
        {filteredVlts.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FaFileAlt size={32} className="text-white" />
            </div>
            <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No VLTs Available</h5>
            <p className="text-gray-600 text-base sm:text-lg">Check back later for new learning resources</p>
          </div>
        ) : (
          <Row className="g-4">
            {filteredVlts.map(vlt => (
              <Col key={vlt.id} md={6} lg={4} sm={12}>
                <Card className="h-100 border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                  {vlt.thumbnail_image ? (
                    <div style={{ height: '200px', overflow: 'hidden' }}>
                      <Card.Img 
                        variant="top" 
                        src={getAssetUrl(vlt.thumbnail_image)}
                        style={{ height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease' }}
                        onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                      />
                    </div>
                  ) : (
                    <div 
                      className="d-flex align-items-center justify-content-center"
                      style={{ 
                        height: '200px',
                        background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)'
                      }}
                    >
                      <div className="text-3xl">
                        {getTypeIcon(vlt.type)}
                      </div>
                    </div>
                  )}
                  <Card.Body className="d-flex flex-column p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <Card.Title className="mb-0 fw-bold text-gray-900">{vlt.title}</Card.Title>
                      <Badge className={`px-3 py-1 rounded-full font-semibold ${
                        vlt.type === 'video' ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white' :
                        vlt.type === 'document' ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white' :
                        'bg-gradient-to-r from-indigo-500 to-purple-600 text-white' 
                      }`}>
                        {vlt.type}
                      </Badge>
                    </div>
                    <Card.Text className="text-gray-600 small mb-3 flex-grow-1" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: '60px'
                    }}>
                      {vlt.description?.substring(0, 150)}
                      {vlt.description?.length > 150 && '...'}
                    </Card.Text>
                    {vlt.type === 'link' && (
                      <div className="mb-3 p-2 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg space-y-1">
                        <small className="text-gray-700 d-block">
                          <strong>Meeting:</strong>{' '}
                          <span className="text-gray-900">{formatMeetingDatetime(vlt.meeting_datetime)}</span>
                        </small>
                        <small className="text-gray-700 d-block">
                          <strong>Instructor:</strong>{' '}
                          <span className="text-gray-900">{getInstructorDisplayName(vlt)}</span>
                        </small>
                      </div>
                    )}
                    {vlt.course && (
                      <div className="mb-3 p-2 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg">
                        <small className="text-gray-700">
                          <strong>Course:</strong> <span className="text-gray-900">{vlt.course.name}</span>
                        </small>
                      </div>
                    )}
                    {vlt.userProgress && vlt.userProgress.length > 0 && (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-gray-600 fw-semibold">Progress:</small>
                          <Badge className={`px-3 py-1 rounded-full font-semibold ${
                            vlt.userProgress[0].status === 'completed' 
                              ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white' :
                            vlt.userProgress[0].status === 'in_progress' 
                              ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white' 
                              : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
                          }`}>
                            {vlt.userProgress[0].status === 'completed' ? 'Completed' :
                             vlt.userProgress[0].status === 'in_progress' ? 'In Progress' : 'Not Started'}
                          </Badge>
                        </div>
                        {vlt.userProgress[0].view_count > 0 && (
                          <small className="text-gray-600">
                            <i className="fas fa-eye me-1 text-purple-600"></i>
                            Views: {vlt.userProgress[0].view_count}
                          </small>
                        )}
                      </div>
                    )}
                    <div className="mt-auto">
                      {(() => {
                        const meetingStatus = vlt.type === 'link'
                          ? getMeetingDateStatus(vlt.meeting_datetime)
                          : null;
                        const isMeetingDisabled = meetingStatus && meetingStatus !== 'today';
                        const meetingButtonLabel = meetingStatus === 'past'
                          ? 'Meeting Ended'
                          : meetingStatus === 'future'
                            ? 'Available on Meeting Day'
                            : 'Join Meeting';

                        return (
                          <>
                            {isMeetingDisabled && (
                              <p className="text-xs text-gray-500 mb-1.5 text-center leading-snug">
                                {meetingStatus === 'past'
                                  ? 'This meeting has already passed.'
                                  : 'Available on the scheduled date.'}
                              </p>
                            )}
                            <Button
                              bsPrefix="custom-hamburger"
                              className="w-full border border-purple-600 bg-white text-purple-600 font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-xs flex items-center justify-center gap-1.5 py-1.5 px-3 disabled:opacity-60"
                              size="sm"
                              disabled={isMeetingDisabled}
                              onClick={() => handleAccessVlt(vlt)}
                              aria-label={
                                vlt.type === 'document'
                                  ? (isHttpUrl(vlt.url_or_path) ? 'Open Document' : 'View Document')
                                  : vlt.type === 'link'
                                    ? meetingButtonLabel
                                    : 'Access Resource'
                              }
                            >
                              <i className={`fas ${vlt.type === 'document' && !isHttpUrl(vlt.url_or_path) ? 'fa-eye' : 'fa-external-link-alt'}`} aria-hidden="true" />
                              {vlt.type === 'document'
                                ? (isHttpUrl(vlt.url_or_path) ? 'Open Document' : 'View Document')
                                : vlt.type === 'link'
                                  ? meetingButtonLabel
                                  : 'Access Resource'}
                            </Button>
                          </>
                        );
                      })()}
                    </div>
                  </Card.Body>
                  <Card.Footer className="text-muted small bg-gray-50 border-0">
                    <small>
                      <i className="fas fa-calendar-alt me-1 text-purple-600"></i>
                      Added {new Date(vlt.createdAt).toLocaleDateString()}
                    </small>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>

      <VltDocumentViewer
        show={showDocumentViewer}
        vlt={viewerVlt}
        onHide={() => {
          setShowDocumentViewer(false);
          setViewerVlt(null);
        }}
      />
    </div>
  );
};

export default UserVltList;
